import React, { Suspense, lazy } from "react";
import { Routes, Route } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext.jsx";
import { useAuthContext } from "./context/authContextInstance";
import { CartProvider } from "./context/CartContext.jsx";
import PrivateRoute from "./components/PrivateRoute.jsx";
import RoleGuard from "./components/RoleGuard.jsx";
import Nav from "./pages/Nav.jsx";
import Footer from "./pages/Footer.jsx";
import { ToastContainer } from "react-toastify";
import { HelmetProvider } from "react-helmet-async";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";

// Componentes críticos cargados normalmente
import Inicio from "./pages/Inicio.jsx";
import Productos from "./pages/Productos.jsx";

// Lazy loading para rutas menos críticas
const Contacto = lazy(() => import("./pages/Contacto.jsx"));
const ProductoDetalle = lazy(() => import("./pages/DetalleProductos.jsx"));
const Carrito = lazy(() => import("./pages/Carrito.jsx"));
const Login = lazy(() => import("./pages/Login.jsx"));
const LoginAdmin = lazy(() => import("./pages/LoginAdmin.jsx"));
const Registro = lazy(() => import("./pages/Registro.jsx"));
const RecuperarPassword = lazy(() => import("./pages/RecuperarPassword.jsx"));
const Dashboard = lazy(() => import("./pages/Dashboard.jsx"));
const FormularioProducto = lazy(() =>
  import("./components/FormularioProductos.jsx")
);

// Componente de carga
const LoadingFallback = () => (
  <div
    style={{
      minHeight: "60vh",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      fontSize: "1.2rem",
      fontFamily: "var(--font-display)",
    }}
  >
    ✨ Cargando... ✨
  </div>
);

function AppContent() {
  const { cargando } = useAuthContext();

  if (cargando) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontSize: "1.5rem",
          fontFamily: "var(--font-display)",
        }}
      >
        ✨ Cargando... ✨
      </div>
    );
  }

  return (
    <div>
      <Nav />
      <main>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Rutas públicas */}
            <Route path="/" element={<Inicio />} />
            <Route path="/Productos" element={<Productos />} />
            <Route path="/Contacto" element={<Contacto />} />
            <Route path="/Carrito" element={<Carrito />} />
            <Route path="/Productos/:id" element={<ProductoDetalle />} />
            <Route path="/login" element={<Login />} />
            <Route path="/admin" element={<LoginAdmin />} />
            <Route path="/registro" element={<Registro />} />
            <Route path="/recuperar-password" element={<RecuperarPassword />} />

            {/* Rutas privadas con roles */}
            {/* Dashboard: accesible SOLO para admin y editor */}
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <RoleGuard rolesPermitidos={["admin", "editor"]}>
                    <Dashboard />
                  </RoleGuard>
                </PrivateRoute>
              }
            />

            {/* Agregar producto: solo admin y editor */}
            <Route
              path="/agregar-producto"
              element={
                <PrivateRoute>
                  <RoleGuard rolesPermitidos={["admin", "editor"]}>
                    <FormularioProducto />
                  </RoleGuard>
                </PrivateRoute>
              }
            />

            {/* Editar producto: solo admin y editor */}
            <Route
              path="/editar-producto/:id"
              element={
                <PrivateRoute>
                  <RoleGuard rolesPermitidos={["admin", "editor"]}>
                    <FormularioProducto />
                  </RoleGuard>
                </PrivateRoute>
              }
            />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
        style={{
          marginTop: "60px",
        }}
      />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <CartProvider>
          <AppContent />
        </CartProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
